import AnimatedPopup from "../core/AnimatedPopup";
import Button from "../core/Button";
import Strings from "../core/data/Strings";
import ResizableScene from "../core/ResizableScene";
import ARIA from "../utils/ARIA";
import Fonts from "../utils/Fonts";
import GameColors from "../utils/GameColors";
import Overlay from "../utils/Overlay";

export class CertificatePopup extends AnimatedPopup {
	private _onResize: () => void;
	scene: ResizableScene;
	particles: Phaser.GameObjects.Image[] = [];
	popup: Phaser.GameObjects.Image;
	extrasContainer: Phaser.GameObjects.Container;
	certificateContainer: Phaser.GameObjects.Container;
	snapShotButton: Button;

	constructor(scene: ResizableScene) {
		super(scene, 0, 0);
		this.scene = scene;
		const overlay = (this._overlay = new Overlay(scene, 0.7, "popupOverlay", 0xffffff));
		this.add(overlay);
		const emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];

		for (const img of ["fallingSnow1", "fallingSnow2", "fallingSnow3"]) {
			const particle = this.scene.add.particles(img);
			this.particles.push();
			const emitter = particle.createEmitter({
				alpha: { start: 1, end: 0.0 },
				scale: { min: 0.7, max: 1 },
				frequency: 300,
				lifespan: 2500,
				gravityY: 300,
				speedX: { min: -50, max: 300 },
				speedY: { min: 150, max: 400 },
			});
			const topLeft = this.scene.viewport.getTopLeft();
			const topRight = this.scene.viewport.getTopRight();
			emitter.setPosition({ min: -topLeft.x, max: topRight.x }, { min: topLeft.y - 20, max: topLeft.y - 20 });

			emitters.push(emitter);
			emitter.start();
			this.add(particle);
		}

		const popupGroup = (this._popupGroup = this.scene.add.container());
		this.add(popupGroup);
		const extrasContainer = (this.extrasContainer = scene.add.container(0, 0));
		popupGroup.add(extrasContainer);

		const popup = (this.popup = scene.add.nineSlicedSprite(0, 0, "popupBG", 1325, 920, 50, 50, 50, 50, null, false));
		popupGroup.add(popup);
		popupGroup.setSize(popup.slicedWidth, popup.slicedHeight);

		const leftDeco = scene.add.image(0, 0, "completeDecoLeft");
		const rightDeco = scene.add.image(0, 0, "completeDecoRight");

		popupGroup.add([leftDeco, rightDeco]);

		const headerBG = scene.add.image(0, 0, "popupHeader");
		const headerText = this.scene.add.text(0, 0, Strings.get("Download").toUpperCase(), {
			color: GameColors.WHITE,
			fontSize: "70px",
			fontFamily: Fonts.MontserratExtraBold,
			align: "center",
		});
		popupGroup.add([headerBG, headerText]);
		this.scene.alignIn.TopCenter(headerText, popup, 0, -25);

		const homeButton = scene.add.button(0, 0, "homeBtn", null, this.onHomePressed, this, true);
		popupGroup.add(homeButton);

		const snapShotButton = (this.snapShotButton = scene.add.button(0, 0, "downloadBtn", null, this.onDownloadPressed, this, true));
		popupGroup.add(snapShotButton);
		snapShotButton.setScale(0.85);

		this._onResize = (): void => {
			scene.alignIn.Center(popupGroup, scene.viewport);
			scene.alignIn.TopCenter(headerBG, popup, 0, headerBG.height / 2);
			scene.alignIn.Center(headerText, headerBG, 0, 0);

			//scene.alignIn.Center(downloadImage, popup, 0, -85);
			this.certificateContainer.setScale(0.65);

			scene.alignIn.BottomLeft(leftDeco, popup, 135, 20);
			scene.alignIn.BottomRight(rightDeco, popup, 190, 30);

			scene.alignTo.BottomCenter(homeButton, popup, 120, -homeButton.height * 0.75);
			scene.alignTo.BottomCenter(snapShotButton, popup, -120, -homeButton.height * 0.85);
		};
		const setCertificateAria = this.createCertificate();
		this.resize();

		const ariaLayer = "cert-popup";
		headerText.setARIA(ariaLayer, true);
		setCertificateAria(ariaLayer);
		snapShotButton.setARIA("Download", ariaLayer);
		homeButton.setARIA("Home", ariaLayer);
		ARIA.showLayer(ariaLayer, true);

		this.once(Phaser.GameObjects.Events.DESTROY, () => ARIA.showLayer(ARIA.DEFAULT_LAYER, true));

		this.intro();
	}

	createCertificate(): (layer: string) => void {
		const certificateContainer = (this.certificateContainer = this.scene.add.container(0, 0));
		this._popupGroup.add(certificateContainer);

		const pinkBacker = this.scene.add.image(0, 0, "pinkBacker");
		pinkBacker.setScale(1.5);
		certificateContainer.add(pinkBacker);
		this.scene.alignIn.Center(pinkBacker, this.popup, 0, -100);

		const bg = this.scene.add.image(0, 0, "completeBG");
		bg.setScale(0.75);
		certificateContainer.add(bg);
		this.scene.alignIn.Center(bg, pinkBacker, 0, 0);

		const subHeader = this.scene.add.image(0, 0, "completeHeader");
		certificateContainer.add(subHeader);
		subHeader.setScale(0.9);
		this.scene.alignIn.TopCenter(subHeader, bg, 0, -128);

		const subHeaderText = this.scene.add.text(0, 0, Strings.get("Congratulations").toUpperCase(), {
			color: GameColors.WHITE,
			fontSize: "60px",
			fontFamily: Fonts.MontserratExtraBold,
			align: "center",
		});
		certificateContainer.add(subHeaderText);
		this.scene.alignIn.Center(subHeaderText, subHeader);

		const logo = this.scene.add.image(0, 0, "loadingLogo");
		certificateContainer.add(logo);
		logo.setScale(0.8);
		this.scene.alignIn.Center(logo, bg, 0, -135);

		const backer = this.scene.add.image(0, 0, "darkBacker");
		certificateContainer.add(backer);
		backer.setScale(0.7);
		this.scene.alignIn.Center(backer, bg, 0, 75);

		const certDescText = this.scene.add.text(0, 0, Strings.get("CertificateDesc"), {
			color: GameColors.WHITE,
			fontSize: "25px",
			fontFamily: Fonts.MontserratBold,
			align: "center",
		});
		certDescText.setLineSpacing(8);
		certificateContainer.add(certDescText);
		this.scene.alignIn.Center(certDescText, backer, 0, 0);

		const medal = this.scene.add.image(0, 0, "popupMedal");
		certificateContainer.add(medal);
		this.scene.alignTo.BottomCenter(medal, backer, 0, -70);

		for (let i = 0; i < 2; i++) {
			const star = this.scene.add.image(0, 0, "completeStars");
			certificateContainer.add(star);
			if (i == 0) {
				this.scene.alignIn.LeftCenter(star, bg, -250, -65);
			} else {
				star.flipX = true;
				this.scene.alignIn.RightCenter(star, bg, -250, -65);
			}
			star.setScale(0.8);
		}

		return (layer: string): void => {
			subHeaderText.setARIA(layer);
			ARIA.addTextWithObject(logo, "Corinne Tan", layer);
			certDescText.setARIA(layer);
		};
	}

	resize(): void {
		if (this._onResize) this._onResize();
		super.resize();
	}

	onHomePressed(): void {
		this.scene.soundManager.stop("ambienceSFX");
		this.scene.scene.start("PreloaderScene", {
			destinationScene: "LevelSelectScene",
		});
	}

	onDownloadPressed(): void {
		this.snapShotButton.disableInteractive();
		this.scene.input.enabled = false;
		this.removeSnapshot();
		this.scene.game.renderer.snapshotArea(this.scene.viewport.width / 2 - 450, 215, 918, 518, (image) => {
			if (image instanceof HTMLImageElement) {
				image.style.display = "none";
				document.body.appendChild(image);
				this.scene.textures.addImage("snapshot", image);
			}
			this.scene.input.enabled = true;
			this.download();
		});
	}

	download(): void {
		const snapshotSource = this.scene.textures.get("snapshot").getSourceImage();
		(<any>window).saveAs((snapshotSource as HTMLImageElement).src, "ski-training-certificate.png");
		this.snapShotButton.setInteractive();
	}

	removeSnapshot(): void {
		if (this.scene.textures.exists("snapshot")) {
			const snapshotSource = this.scene.textures.get("snapshot").getSourceImage() as HTMLImageElement;
			snapshotSource.remove();
			this.scene.textures.remove("snapshot");
		}
	}
}
